"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { listDashboards, listDashboardWidgets } from "@/lib/api/endpoints/dashboards";
import { getDashboardLayoutKind } from "@/modules/dashboards/hmi/utils/layout-kind";

export function useWorkspaceDashboard(
  tenantId: string | null,
  requestedDashboardId?: string | null,
  options: { selectDefault?: boolean } = {},
) {
  const dashboardsQuery = useQuery({
    queryKey: ["dashboards", tenantId ?? "", "accessible"],
    queryFn: () => listDashboards(tenantId!, "accessible"),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const dashboards = dashboardsQuery.data ?? [];
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const selectDefault = options.selectDefault ?? true;

  useEffect(() => {
    if (dashboards.length === 0) {
      setSelectedDashboardId(null);
      return;
    }
    if (requestedDashboardId && dashboards.some((dashboard) => dashboard.id === requestedDashboardId)) {
      setSelectedDashboardId(requestedDashboardId);
      return;
    }
    if (!selectDefault) {
      setSelectedDashboardId(null);
      return;
    }
    if (!selectedDashboardId || !dashboards.some((dashboard) => dashboard.id === selectedDashboardId)) {
      setSelectedDashboardId(dashboards[0]!.id);
    }
  }, [dashboards, requestedDashboardId, selectDefault, selectedDashboardId]);

  const selectedDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === selectedDashboardId) ?? null,
    [dashboards, selectedDashboardId],
  );

  const widgetsQuery = useQuery({
    queryKey: ["dashboard-widgets", selectedDashboard?.id ?? ""],
    queryFn: () => listDashboardWidgets(selectedDashboard!.id),
    enabled: Boolean(selectedDashboard?.id && getDashboardLayoutKind(selectedDashboard.layout) === "classic"),
    staleTime: 30_000,
  });

  return {
    dashboardsQuery,
    dashboards,
    widgetsQuery,
    selectedDashboard,
    selectedDashboardId,
    setSelectedDashboardId,
  };
}
